import { computed, watch, type ComputedRef, type Ref } from 'vue'
import { buildEquipePagePath } from '~/composables/equipe/useEquipe'
import type { useConcurrentForm } from '~/composables/orga/useConcurrentForm'
import { getErrorMessage } from '~/composables/ui/useFormError'
import { useDiscardUnsavedGuard } from '~/composables/ui/useDiscardUnsavedGuard'
import type { FormValidationFeedback } from '~/composables/ui/useFormValidationFeedback'
import type { IEquipe } from '~/models/Equipe'
import type { OrganizationKey } from '~~/errors/ConcurrentChangeError'
import type { MergeRecord } from '~~/utils/organization/concurrentPatch'
import type {
  CreateEquipeFormProps,
  EquipeContactRow,
} from '~/composables/equipe/useCreateEquipeForm'

type EquipeRoutePayload = {
  dirLib: string
  domLib: string
  ageLib: string
  equLib: string
}

type EquipeDraft = EquipeRoutePayload & {
  equDesc: string
  equMis: string
  contacts: EquipeContactRow[]
}

type CreateEquipeFormEmit = {
  (event: 'close' | 'refetch'): void
  (event: 'created' | 'deleted', payload: EquipeRoutePayload): void
}

export type CreateEquipeFormContext = {
  props: CreateEquipeFormProps
  emit: CreateEquipeFormEmit
  hasUnsavedChanges: Ref<boolean>
  storeEquipe: (payload: EquipeDraft) => Promise<Partial<EquipeRoutePayload> | null | undefined>
  patchEquipe: (
    key: string,
    payload: EquipeDraft | MergeRecord,
    base?: MergeRecord,
  ) => Promise<(Partial<EquipeRoutePayload> & { currentKey?: OrganizationKey | null }) | null | undefined>
  concurrent: ReturnType<typeof useConcurrentForm>
  fetchEquipeById: (
    dirLib: string,
    domLib: string,
    ageLib: string,
    equLib: string,
    options?: { page?: number; limit?: number; includeHidden?: boolean },
  ) => Promise<{ equipe?: IEquipe | null } | null>
  deleteEquipe: (key: string) => Promise<boolean>
  toast: { success: (title: string, message: string) => void }
  feedback: FormValidationFeedback
  loading: Ref<boolean>
  loadDone: Ref<boolean>
  showDeleteConfirm: Ref<boolean>
  equipeKeyToPatch: Ref<string>
  contactsTableRef: Ref<{ hideUndoBanner: () => void } | null>
  equipe: Ref<EquipeDraft>
  captureSnapshot: () => void
  effectiveDefaultEquLib: ComputedRef<string>
}

function hasContactContent(contact: EquipeContactRow): boolean {
  return (contact.conLib ?? '').trim() !== '' || (contact.conChamp ?? '').trim() !== ''
}

function isNonClassicEquipeSegments(segments: { ageLib: string; equLib: string }): boolean {
  return segments.ageLib === segments.equLib
}

function nonClassicSegmentsFromEditKey(key: string | undefined): boolean {
  if (!key || !key.includes('~')) return false
  const parts = key.split('~')
  if (parts.length < 4) return false
  return parts[2] === parts[3]
}

function resetForm(ctx: CreateEquipeFormContext) {
  ctx.feedback.clearAll()
  ctx.loadDone.value = false
  const defaultLib = ctx.effectiveDefaultEquLib.value || ''
  ctx.equipe.value = {
    dirLib: ctx.props.directionName,
    domLib: ctx.props.domaineName,
    ageLib: ctx.props.agenceName,
    equLib: defaultLib,
    equDesc: '',
    equMis: '',
    contacts: [],
  }
}

function hideUndoBanner(ctx: CreateEquipeFormContext) {
  ctx.contactsTableRef.value?.hideUndoBanner()
}

function discardAndClose(ctx: CreateEquipeFormContext) {
  hideUndoBanner(ctx)
  ctx.hasUnsavedChanges.value = false
  ctx.emit('close')
  resetForm(ctx)
  ctx.hasUnsavedChanges.value = false
}

function closeOverlayAfterSuccess(
  ctx: CreateEquipeFormContext,
  createdEquipe?: EquipeRoutePayload,
  options?: { refetch?: boolean },
) {
  if (createdEquipe) ctx.emit('created', createdEquipe)
  if (options?.refetch !== false) ctx.emit('refetch')
  ctx.emit('close')
  resetForm(ctx)
}

async function loadEquipeToEdit(ctx: CreateEquipeFormContext, key: string) {
  ctx.equipeKeyToPatch.value = key
  ctx.loadDone.value = false
  const parts = key.includes('~') ? key.split('~') : []
  const [dirLib, domLib, ageLib, equLib] = parts.length >= 4
    ? [parts[0] ?? '', parts[1] ?? '', parts[2] ?? '', parts[3] ?? '']
    : [ctx.props.directionName, ctx.props.domaineName, ctx.props.agenceName, key]

  try {
    const data = await ctx.fetchEquipeById(dirLib, domLib, ageLib, equLib)
    const loaded = data?.equipe
    ctx.equipe.value = loaded
      ? {
          dirLib: loaded.dirLib,
          domLib: loaded.domLib,
          ageLib: loaded.ageLib,
          equLib: loaded.equLib,
          equDesc: loaded.equDesc ?? '',
          equMis: loaded.equMis ?? '',
          contacts: (loaded.contacts ?? []).filter(hasContactContent),
        }
      : { dirLib, domLib, ageLib, equLib: equLib || ctx.props.directionName, equDesc: '', equMis: '', contacts: [] }
    ctx.concurrent.rememberBase({ ...ctx.equipe.value, contacts: [...ctx.equipe.value.contacts] })
  } catch (error: unknown) {
    ctx.feedback.notifyNonFieldError(
      'Chargement impossible',
      error instanceof Error ? error.message : 'Erreur lors du chargement.',
    )
    ctx.equipe.value = {
      dirLib,
      domLib,
      ageLib,
      equLib: equLib || ctx.props.directionName,
      equDesc: '',
      equMis: '',
      contacts: [],
    }
  } finally {
    ctx.loadDone.value = true
    ctx.captureSnapshot()
  }
}

function lockEquLibWhenAgeEqualsEqu(ctx: CreateEquipeFormContext, key: string): void {
  const keySegments = key.split('~')
  if (keySegments.length < 4) return
  const keyAgeLib = keySegments[2] ?? ''
  const keyEquLib = keySegments[3] ?? ''
  if (keyAgeLib === keyEquLib) ctx.equipe.value.equLib = keyEquLib
}

function prepareEquipeSubmitPayload(ctx: CreateEquipeFormContext, filledContacts: EquipeContactRow[]): EquipeDraft {
  ctx.equipe.value.equLib = ctx.equipe.value.equLib.trim()
  ctx.equipe.value.equDesc = (ctx.equipe.value.equDesc ?? '').trim() || ''
  ctx.equipe.value.equMis = (ctx.equipe.value.equMis ?? '').trim() || ''
  if (ctx.props.isEditing && ctx.equipeKeyToPatch.value) {
    lockEquLibWhenAgeEqualsEqu(ctx, ctx.equipeKeyToPatch.value)
  }
  const trimmedContacts = filledContacts.map((contact) => ({
    conLib: (contact.conLib ?? '').trim(),
    conChamp: (contact.conChamp ?? '').trim(),
  }))
  ctx.equipe.value.contacts = [...trimmedContacts]
  return { ...ctx.equipe.value, contacts: trimmedContacts }
}

function routeIdentityChanged(originalKey: string, updated: EquipeRoutePayload): boolean {
  const originalParts = originalKey.split('~')
  return originalParts[0] !== updated.dirLib
    || originalParts[1] !== updated.domLib
    || originalParts[2] !== updated.ageLib
    || originalParts[3] !== updated.equLib
}

const EQUIPE_MERGE_FIELDS = ['dirLib', 'domLib', 'ageLib', 'equLib', 'equDesc', 'equMis', 'contacts'] as const

async function persistEditedEquipe(ctx: CreateEquipeFormContext, payload: EquipeDraft): Promise<boolean> {
  if (!ctx.equipeKeyToPatch.value) {
    ctx.feedback.notifyNonFieldError(
      'Enregistrement impossible',
      "Impossible de modifier : identifiant d'équipe manquant.",
    )
    return false
  }
  const envelope = ctx.concurrent.buildPatch(payload, EQUIPE_MERGE_FIELDS)
  if (envelope === null) {
    ctx.toast.success('Équipe modifiée', `« ${payload.equLib} » a été mise à jour.`)
    closeOverlayAfterSuccess(ctx)
    return true
  }
  const updated = await ctx.patchEquipe(ctx.equipeKeyToPatch.value, envelope.changes, envelope.base)
  const renamed = await ctx.concurrent.noteParentRename(
    ctx.equipeKeyToPatch.value,
    updated?.currentKey,
    envelope.changes,
  )
  const updatedEquipe: EquipeRoutePayload = {
    dirLib: updated?.dirLib ?? payload.dirLib,
    domLib: updated?.domLib ?? payload.domLib,
    ageLib: updated?.ageLib ?? payload.ageLib,
    equLib: updated?.equLib ?? payload.equLib,
  }
  if (!renamed && routeIdentityChanged(ctx.equipeKeyToPatch.value, updatedEquipe)) {
    await navigateTo(buildEquipePagePath(updatedEquipe), { replace: true })
  }
  if (!renamed) {
    ctx.toast.success('Équipe modifiée', `« ${updatedEquipe.equLib} » a été mise à jour.`)
  }
  closeOverlayAfterSuccess(ctx, undefined, { refetch: renamed ? false : !routeIdentityChanged(ctx.equipeKeyToPatch.value, updatedEquipe) })
  return true
}

async function persistCreatedEquipe(ctx: CreateEquipeFormContext, payload: EquipeDraft): Promise<void> {
  const created = await ctx.storeEquipe(payload)
  const routeIdentity: EquipeRoutePayload = {
    dirLib: created?.dirLib ?? payload.dirLib,
    domLib: created?.domLib ?? payload.domLib,
    ageLib: created?.ageLib ?? payload.ageLib,
    equLib: created?.equLib ?? payload.equLib,
  }
  ctx.toast.success(
    'Équipe créée',
    routeIdentity.equLib
      ? `« ${routeIdentity.equLib} » a été ajoutée.`
      : 'La nouvelle équipe a été enregistrée.',
  )
  closeOverlayAfterSuccess(ctx, routeIdentity)
}

function handleEquipeSubmitError(ctx: CreateEquipeFormContext, error: unknown): void {
  if (ctx.concurrent.captureError(error)) return
  if (ctx.feedback.applyFromUnknown(error)) {
    ctx.feedback.notifySubmitFailure('api')
    ctx.feedback.focusFirstInvalid()
    return
  }
  ctx.feedback.notifyNonFieldError('Enregistrement impossible', getErrorMessage(error))
}

async function validateForm(ctx: CreateEquipeFormContext, filledContacts: EquipeContactRow[]) {
  hideUndoBanner(ctx)
  ctx.feedback.clearAll()
  ctx.loading.value = true
  const payload = prepareEquipeSubmitPayload(ctx, filledContacts)
  try {
    if (!payload.equLib) {
      ctx.feedback.setFieldError('equLib', "Le nom de l'équipe est obligatoire.")
      ctx.feedback.notifySubmitFailure('client')
      ctx.feedback.focusFirstInvalid()
      return
    }
    if (ctx.props.isEditing) {
      await persistEditedEquipe(ctx, payload)
      return
    }
    await persistCreatedEquipe(ctx, payload)
  } catch (error: unknown) {
    handleEquipeSubmitError(ctx, error)
  } finally {
    ctx.loading.value = false
  }
}

async function onConfirmDelete(ctx: CreateEquipeFormContext) {
  hideUndoBanner(ctx)
  if (!ctx.props.isEditing || !ctx.equipeKeyToPatch.value) return
  ctx.feedback.clearAll()
  ctx.loading.value = true
  try {
    const deleted = await ctx.deleteEquipe(ctx.equipeKeyToPatch.value)
    if (deleted) {
      ctx.showDeleteConfirm.value = false
      ctx.toast.success('Équipe supprimée', `« ${ctx.equipe.value.equLib} » a été retirée.`)
      ctx.emit('deleted', {
        dirLib: ctx.equipe.value.dirLib,
        domLib: ctx.equipe.value.domLib,
        ageLib: ctx.equipe.value.ageLib,
        equLib: ctx.equipe.value.equLib,
      })
      discardAndClose(ctx)
      return
    }
    ctx.feedback.notifyNonFieldError('Suppression impossible', 'Impossible de supprimer l’équipe.')
  } catch (error: unknown) {
    ctx.feedback.notifyNonFieldError(
      'Suppression impossible',
      error instanceof Error ? error.message : 'Erreur lors de la suppression.',
    )
  } finally {
    ctx.loading.value = false
  }
}

export function bindCreateEquipeForm(ctx: CreateEquipeFormContext) {
  const filledContacts = computed(() => (ctx.equipe.value.contacts ?? []).filter(hasContactContent))
  const hideEquipeNameField = computed(() => {
    if (ctx.effectiveDefaultEquLib.value) return true
    if (!ctx.props.isEditing) return false
    if (!ctx.loadDone.value) return nonClassicSegmentsFromEditKey(ctx.props.equipeToEdit)
    return isNonClassicEquipeSegments(ctx.equipe.value)
  })
  const { showDiscardConfirm, requestClose, confirmDiscard, cancelDiscard } = useDiscardUnsavedGuard({
    isDirty: computed(() => ctx.hasUnsavedChanges.value),
    onDiscard: () => discardAndClose(ctx),
    loading: ctx.loading,
  })

  watch(
    () => ctx.props.showOverlay,
    function captureSnapshotWhenOverlayOpens(show) {
      if (!show || ctx.props.isEditing) return
      ctx.captureSnapshot()
    },
  )
  watch(
    () => [
      ctx.props.isEditing,
      ctx.props.equipeToEdit,
      ctx.props.directionName,
      ctx.props.domaineName,
      ctx.props.agenceName,
      ctx.props.defaultEquLib,
    ] as const,
    async function syncEquipeForm([isEditing, equipeKey, directionName, domaineName, agenceName]) {
      resetForm(ctx)
      ctx.equipeKeyToPatch.value = ''
      if (isEditing && equipeKey) {
        await loadEquipeToEdit(ctx, equipeKey)
        return
      }
      ctx.equipe.value.dirLib = directionName
      ctx.equipe.value.domLib = domaineName
      ctx.equipe.value.ageLib = agenceName
      if (ctx.effectiveDefaultEquLib.value) ctx.equipe.value.equLib = ctx.effectiveDefaultEquLib.value
      ctx.loadDone.value = true
      ctx.captureSnapshot()
    },
    { immediate: true },
  )

  return {
    hideEquipeNameField,
    showDiscardConfirm,
    requestClose,
    confirmDiscard,
    cancelDiscard,
    validateForm: () => validateForm(ctx, filledContacts.value),
    onConfirmDelete: () => onConfirmDelete(ctx),
  }
}
